import React, { useEffect, useState } from "react";
import { Plus, X, Edit2, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import { dltBanner, getBanner, postBanner, putBanner } from "../../services";



function Banners() {
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [updatingBannerId, setUpdatingBannerId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    button_text: "",
    button_link: "",
    is_active: true,
    display_order: 1,
    image_sm: null,
    image_md: null,
    image_lg: null,
  });

  // Previews for file inputs
  const [previews, setPreviews] = useState({
    image_sm: "",
    image_md: "",
    image_lg: "",
  });

  const getNextDisplayOrder = () =>
    banners.reduce(
      (maxOrder, banner, index) =>
        Math.max(maxOrder, Number(banner.display_order) || index + 1),
      0
    ) + 1;

  const getOthersWithDisplayOrder = (banner, displayOrder) => ({
    ...(banner?.others && typeof banner.others === "object" && !Array.isArray(banner.others)
      ? banner.others
      : {}),
    display_order: Number(displayOrder),
  });

  // ---------------- FETCH ----------------
  const fetchBanners = async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    try {
      const res = await getBanner();
      //   console.log("Fetched banners:", res.data);
      setBanners(res);
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // ---------------- HANDLERS ----------------
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files[0]) {
      const file = files[0];
      setForm({ ...form, [name]: file });
      // Create local preview URL
      setPreviews({ ...previews, [name]: URL.createObjectURL(file) });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const openModal = (banner = null) => {
    if (banner) {
      setEditingId(banner.id);
      setForm({
        title: banner.title,
        description: banner.description,
        button_text: banner.button_text,
        button_link: banner.button_link,
        is_active: banner.is_active,
        display_order: banner.display_order || Math.max(1, banners.findIndex((item) => item.id === banner.id) + 1),
        image_sm: null, // Reset file inputs on edit (backend keeps old unless replaced)
        image_md: null,
        image_lg: null,
      });
      setPreviews({
        image_sm: banner.image_sm || "",
        image_md: banner.image_md || "",
        image_lg: banner.image_lg || "",
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      button_text: "",
      button_link: "",
      is_active: true,
      display_order: getNextDisplayOrder(),
      image_sm: null,
      image_md: null,
      image_lg: null,
    });
    setPreviews({ image_sm: "", image_md: "", image_lg: "" });
    setEditingId(null);
  };

  // ---------------- CREATE / UPDATE ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(form).forEach((key) => {
      if (key !== "display_order" && form[key] !== null && form[key] !== "") {
        data.append(key, form[key]);
      }
    });
    const editingBanner = banners.find((banner) => banner.id === editingId);
    data.append(
      "others",
      JSON.stringify(getOthersWithDisplayOrder(editingBanner, form.display_order))
    );

    try {
      if (editingId) {
        await putBanner(editingId, data);
      } else {
        await postBanner(data);
      }
      closeModal();
      await fetchBanners();
    } catch (error) {
      console.error("Error saving banner:", error);
      const message = error.response?.data?.detail || "Failed to save banner. Please try again.";
      alert(message);
    }
  };

  // ---------------- DELETE ----------------
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    try {
      await dltBanner(id);
      fetchBanners();
    } catch (error) {
      console.error("Error deleting banner:", error);
    }
  };

  const updateBannerField = async (banner, field, value) => {
    setUpdatingBannerId(banner.id);
    try {
      const data = new FormData();
      if (field === "display_order") {
        data.append(
          "others",
          JSON.stringify(getOthersWithDisplayOrder(banner, value))
        );
      } else {
        data.append(field, value);
      }
      await putBanner(banner.id, data);
      await fetchBanners(false);
    } catch (error) {
      console.error(`Error updating banner ${field}:`, error);
      alert(error.response?.data?.detail || "Failed to update banner. Please try again.");
    } finally {
      setUpdatingBannerId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8 font-sans text-gray-800">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Banners</h1>
            <p className="text-gray-500 mt-1">Manage your website's hero banners</p>
          </div>
          <button
            onClick={() => openModal()}
            disabled={banners.length >= 10}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg shadow-sm transition-all"
          >
            <Plus size={20} /> {banners.length >= 10 ? "Maximum 10 Banners" : "Add New Banner"}
          </button>
        </div>

        {/* LOADING STATE */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="animate-spin text-blue-600" size={40} />
          </div>
        ) : (
          /* BANNER GRID */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {banners?.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-400 bg-white rounded-xl border border-dashed border-gray-300">
                <ImageIcon className="mx-auto mb-3 opacity-50" size={48} />
                <p>No banners found. Create one to get started.</p>
              </div>
            )}

            {banners?.map((banner, index) => (
              <div
                key={banner.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group"
              >
                {/* Preview Image (Large) */}
                <div className="h-40 bg-gray-100 relative overflow-hidden">
                  {banner.image_lg ? (
                    <img
                      src={banner.image_lg}
                      alt={banner.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-300">
                      <ImageIcon size={32} />
                    </div>
                  )}

                  {/* Overlay Actions */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <button
                      onClick={() => openModal(banner)}
                      className="p-2 bg-white text-gray-900 rounded-full hover:bg-blue-50 transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(banner.id)}
                      className="p-2 bg-white text-red-600 rounded-full hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <p className="text-xs font-medium text-blue-600 mb-1">
                    Position {banner.display_order || index + 1}
                    {!banner.is_active && <span className="ml-2 text-gray-500">Inactive</span>}
                  </p>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      Position
                      <select
                        aria-label={`Position for ${banner.title}`}
                        value={banner.display_order || index + 1}
                        disabled={updatingBannerId === banner.id}
                        onChange={(event) =>
                          updateBannerField(banner, "display_order", event.target.value)
                        }
                        className="rounded border border-gray-300 px-2 py-1"
                      >
                        {Array.from({ length: 10 }, (_, positionIndex) => (
                          <option key={positionIndex + 1} value={positionIndex + 1}>
                            {positionIndex + 1}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(banner.is_active)}
                        disabled={updatingBannerId === banner.id}
                        onChange={(event) =>
                          updateBannerField(banner, "is_active", event.target.checked)
                        }
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      Show on website
                    </label>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-1 truncate">{banner.title}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-4 h-10">
                    {banner.description || "No description provided."}
                  </p>

                  {/* Thumbnails of other sizes */}
                  {/* <div className="flex gap-2 border-t pt-4 mt-2">
                    <div className="text-xs text-gray-400 mb-1 w-full">Available sizes:</div>
                    {['sm', 'md', 'lg'].map(size => (
                       <span 
                         key={size}
                         className={`text-xs px-2 py-1 rounded ${banner[`image_${size}`] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}
                       >
                         {size.toUpperCase()}
                       </span>
                    ))}
                  </div> */}

                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---------------- MODAL POPUP ---------------- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">

            {/* Modal Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b flex justify-between items-center z-10">
              <h2 className="text-xl font-bold text-gray-800">
                {editingId ? "Edit Banner" : "Create New Banner"}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">

              {/* Text Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="col-span-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  // placeholder="e.g. Summer Sale 2026"
                  // required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display position</label>
                  <input
                    type="number"
                    name="display_order"
                    min="1"
                    max="10"
                    step="1"
                    required
                    value={form.display_order}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  />
                </div>

                <label className="flex items-center gap-2 self-end pb-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={Boolean(form.is_active)}
                    onChange={(event) =>
                      setForm({ ...form, is_active: event.target.checked })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Visible on website
                </label>

                <div className="col-span-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                    placeholder="Brief description of the banner..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                  <input
                    name="button_text"
                    value={form.button_text}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  // placeholder="e.g. Shop Now"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                  <input
                    name="button_link"
                    value={form.button_link}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="https://example.com/sale"
                  />
                </div>
              </div>

              {/* Image Uploads */}
              <div className="space-y-4 pt-2">
                <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Banner Images</h3>

                {['sm', 'md', 'lg'].map((size) => (
                  <div key={size} className="flex items-center gap-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="h-16 w-16 bg-gray-200 rounded-md overflow-hidden flex-shrink-0 border">
                      {previews[`image_${size}`] ? (
                        <img
                          src={previews[`image_${size}`]}
                          alt="Preview"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-gray-400">
                          <ImageIcon size={20} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {/* Image ({size.toUpperCase()}) */}
                        Image ({size === "sm" ? "Mobile 300 X 200 px" : size === "md" ? "Tab 1600 X 1200 px" : "Laptop 5000 X 1000 px"})
                      </label>
                      <input
                        type="file"
                        name={`image_${size}`}
                        onChange={handleChange}
                        accept="image/*"
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="pt-4 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-all"
                >
                  {editingId ? "Save Changes" : "Create Banner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Banners;
